import { Box } from "@mui/material";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useEffect, useMemo, useRef } from "react";

/** Rows kept mounted above and below the viewport so a fast scroll doesn't reveal blank space. */
const overscanRows = 5;

/** Below this many rows a bottom-anchored list renders plainly, pinned to the bottom by flexbox. */
const bottomAnchoredVirtualizationFloor = 5;

/**
 * How tall a row the virtualizer has not measured yet is assumed to be, in px: one size for every row, or a size
 * read from the row itself when a list mixes rows of very different heights.
 *
 * The closer the estimate, the smaller the scroll correction when the row is first measured, which is what a
 * reader scrolling back through unmeasured rows sees as a jump.
 */
export type RowSizeEstimate<T> = number | ((item: T, index: number) => number);

/**
 * The key the virtualizer caches a row's measured height under: the row's own `key`, else its `id`, else its index.
 *
 * Feed rows carry `key` (`item:<id>`, `tmsEvent:<id>`, `dateHeader:<day>`) and picker rows carry `id`. Keyed by
 * index, a prepended page would hand every cached height to whichever row now sits at that index and the anchor row
 * could not be re-found, so a row without either falls back to its index only because nothing better exists.
 */
export function rowKey(item: unknown, index: number): string | number {
  if (typeof item !== "object" || item === null) return index;
  if ("key" in item && (typeof item.key === "string" || typeof item.key === "number")) return item.key;
  if ("id" in item && (typeof item.id === "string" || typeof item.id === "number")) return item.id;
  return index;
}

/**
 * The scroll handle `VirtualizedList` publishes through `refValue`.
 *
 * Ours rather than the virtualization library's: the feed provider, the feed itself and the session
 * recorder footer all hold this handle, so typing them against a library's own handle makes changing
 * virtualizer a cross-cutting change instead of a local one.
 */
export interface VirtualListHandle {
  /** Scrolls the row at `index` into view. Aligns to the top of the viewport unless told otherwise. */
  scrollToIndex: (index: number, options?: { align?: "start" | "center" | "end" | "auto"; smooth?: boolean }) => void;
  /** Scrolls the viewport to an absolute offset from the top of the list, in px. Clamped to the list. */
  scrollToOffset: (offset: number, options?: { smooth?: boolean }) => void;
  /** Where the viewport currently sits, in px from the top of the list. */
  readonly scrollOffset: number;
  /** Height of the visible viewport, in px. */
  readonly viewportSize: number;
  /** Height of the whole list, in px: measured rows plus estimates for the rows never rendered yet. */
  readonly scrollSize: number;
}

/**
 * Virtualized list wrapper over TanStack Virtual, mounting only the rows near the viewport.
 *
 * `reverse` marks the list as bottom-anchored — a message feed rather than a picker — which turns on
 * the virtualizer's scroll anchoring so that prepending older rows keeps the viewport over the row
 * the reader was looking at, and keeps a reader already at the bottom pinned there as rows append.
 */
export default function VirtualizedList<T>({
  id,
  itemSize = 40,
  reverse = false,
  childComponent,
  data,
  emptyState,
  refValue,
  onScroll,
  onScrollEnd,
  className,
}: {
  id: string;
  itemSize?: RowSizeEstimate<T>;
  reverse?: boolean;
  childComponent: React.FC<any>;
  data: T[];
  emptyState?: React.JSX.Element;
  refValue?: React.MutableRefObject<VirtualListHandle | undefined>;
  onScroll?: ({ startIndex, endIndex }: { startIndex: number; endIndex: number }) => void;
  onScrollEnd?: () => void;
  className?: string;
}) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const itemCount = data?.length ?? 0;

  const virtualizer = useVirtualizer({
    count: itemCount,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) => (typeof itemSize === "function" ? itemSize(data[index], index) : itemSize),
    overscan: overscanRows,
    // Row heights are cached by key, so keying on the row's own id rather than its index is what
    // keeps a prepended page of history from re-attributing every measured height to the wrong row —
    // and what lets the virtualizer re-find the anchor row on the other side of the prepend.
    getItemKey: (index) => rowKey(data?.[index], index),
    // `end` turns on scroll anchoring: whenever the row set changes, the virtualizer records the row
    // at the top of the viewport and restores the viewport onto it once the new rows are laid out.
    // That is what a feed needs when a history page prepends, and it needs no per-render "was this a
    // prepend?" hint from the caller, because re-finding the anchor row is the right answer whichever
    // end grew. `followOnAppend` keeps a reader who is already at the bottom pinned there.
    anchorTo: reverse ? "end" : "start",
    followOnAppend: reverse,
  });

  // Which branch renders below. The handle effect reads these too, so the published handle cannot
  // drift out of step with whether there is a virtualized scroll container for it to drive.
  const rendersShortBottomAnchored = reverse && itemCount > 0 && itemCount < bottomAnchoredVirtualizationFloor;
  const rendersVirtualized = itemCount > 0 && !rendersShortBottomAnchored;

  const virtualRows = virtualizer.getVirtualItems();
  const visibleRange = virtualizer.range;
  const startIndex = visibleRange?.startIndex ?? 0;
  const endIndex = visibleRange?.endIndex ?? 0;
  const isScrolling = virtualizer.isScrolling;

  // Consumers pass these inline, so they are new functions on every parent render. Holding them in
  // refs keeps the effects below keyed on scroll state alone rather than firing on every render.
  const onScrollRef = useRef(onScroll);
  onScrollRef.current = onScroll;
  const onScrollEndRef = useRef(onScrollEnd);
  onScrollEndRef.current = onScrollEnd;

  // The feed reads an unreported range as "I don't know where the viewport is, load the newest
  // page". Publishing the mount-time range would instead point it at index 0 — the oldest end — and
  // send it fetching content the reader is scrolling away from, since the open-to-bottom scroll has
  // not landed yet. So ranges are published only once the list has actually scrolled.
  const hasScrolledRef = useRef(false);
  useEffect(() => {
    if (isScrolling) {
      hasScrolledRef.current = true;
      return;
    }
    if (hasScrolledRef.current) {
      onScrollEndRef.current?.();
    }
  }, [isScrolling]);

  useEffect(() => {
    if (itemCount === 0) {
      // Switching feeds empties the list and unmounts the scroll container; its replacement mounts at
      // the top, so close the gate again and let the new data set earn it the way the first one did.
      hasScrolledRef.current = false;
      return;
    }
    if (!hasScrolledRef.current) {
      return;
    }
    onScrollRef.current?.({ startIndex, endIndex });
  }, [startIndex, endIndex, itemCount]);

  const handle = useMemo<VirtualListHandle>(
    () => ({
      scrollToIndex: (index, options) =>
        virtualizer.scrollToIndex(index, {
          align: options?.align ?? "start",
          behavior: options?.smooth ? "smooth" : "auto",
        }),
      scrollToOffset: (offset, options) =>
        virtualizer.scrollToOffset(offset, { behavior: options?.smooth ? "smooth" : "auto" }),
      get scrollOffset() {
        return virtualizer.scrollOffset ?? 0;
      },
      get viewportSize() {
        return virtualizer.scrollRect?.height ?? 0;
      },
      get scrollSize() {
        return virtualizer.getTotalSize();
      },
    }),
    [virtualizer],
  );

  // Published only while the virtualized branch renders, so a caller's `if (vListRef.current)` guard
  // still means "there is something to scroll" — the empty and short-list branches drive no
  // virtualizer, and a handle over them would answer every call with a silent no-op.
  //
  // From an effect rather than a layout effect: everything that calls the handle — the feed's scroll
  // effects, the recorder footer, `scrollToBottomOfFeed` — runs from effects or event handlers, and a
  // child's effects flush before its parent's, so the handle is in place first.
  useEffect(() => {
    if (!refValue || !rendersVirtualized) {
      return;
    }
    refValue.current = handle;
    return () => {
      refValue.current = undefined;
    };
  }, [handle, refValue, rendersVirtualized]);

  const resolvedEmptyState = emptyState ?? (
    <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>No items yet</Box>
  );

  if (itemCount === 0) {
    return resolvedEmptyState;
  }

  if (rendersShortBottomAnchored) {
    return (
      <div
        style={{
          display: "block",
          overflowY: "auto",
          contain: "strict",
          width: "100%",
          height: "100%",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
            minHeight: "100%",
            overflow: "clip",
          }}
        >
          {data?.map((item, index) => (
            <div key={`${id}-row-${rowKey(item, index)}`}>{item ? childComponent(item, index) : null}</div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={scrollRef}
      id={id}
      className={className}
      style={{
        display: "block",
        overflowY: "auto",
        contain: "strict",
        width: "100%",
        height: "100%",
      }}
    >
      <div
        style={{
          position: "relative",
          width: "100%",
          height: virtualizer.getTotalSize(),
          // The browser's own scroll anchoring would compete with the virtualizer's, re-anchoring on
          // a row that absolute positioning is about to move anyway.
          overflowAnchor: "none",
        }}
      >
        {virtualRows.map((virtualRow) => {
          const item = data[virtualRow.index];
          return (
            <div
              key={virtualRow.key}
              data-index={virtualRow.index}
              ref={virtualizer.measureElement}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              {item ? childComponent(item, virtualRow.index) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
